/**
 * Zod decode for `GET /v1/me/entitlements` (zw-cloud wire → app DTO).
 */

import { z } from "zod";
import type { CloudEntitlement, EntitlementsResponse } from "../cloudAuth";
import { ok, type Result } from "../result";
import { decodeWithSchema } from "./zodResult";

const entitlementWireSchema = z.object({
  kind: z.string(),
  status: z.string(),
  active: z.boolean(),
  current_period_end: z.union([z.string(), z.null()]).optional(),
});

const entitlementsResponseWireSchema = z.object({
  entitlements: z.array(entitlementWireSchema),
  issued_at: z.string(),
  grace_until: z.string(),
});

function toCloudEntitlement(
  wire: z.infer<typeof entitlementWireSchema>,
): CloudEntitlement {
  if (typeof wire.current_period_end === "undefined") {
    return {
      kind: wire.kind,
      status: wire.status,
      active: wire.active,
    };
  }
  return {
    kind: wire.kind,
    status: wire.status,
    active: wire.active,
    currentPeriodEnd: wire.current_period_end,
  };
}

/** Decode untrusted `/v1/me/entitlements` JSON into the app DTO. */
export function decodeEntitlementsResponse(
  raw: unknown,
): Result<EntitlementsResponse, string> {
  const wire = decodeWithSchema(
    entitlementsResponseWireSchema,
    raw,
    "entitlements",
  );
  if (wire.ok === false) return wire;
  return ok({
    entitlements: wire.value.entitlements.map(toCloudEntitlement),
    issuedAt: wire.value.issued_at,
    graceUntil: wire.value.grace_until,
  });
}
